import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { utils, writeFile } from "xlsx";
import { DatePicker } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import dayjs, { Dayjs } from "dayjs";
import { getUnsubscribeStats } from "../../../services/partner.service.ts";

const { RangePicker } = DatePicker;

interface InformacionColaboradoresProps {
  unsubscribeStats: any;
  unsubscribeDays: number;
  setUnsubscribeDays: (days: number) => void;
  setUnsubscribeStats: (stats: any) => void;
  onDateRangeChange?: (startDate?: string, endDate?: string) => void;
}

const InformacionColaboradores: React.FC<InformacionColaboradoresProps> = ({
  unsubscribeStats,
  unsubscribeDays,
  setUnsubscribeDays,
  setUnsubscribeStats,
  onDateRangeChange,
}) => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = React.useState<[Dayjs, Dayjs] | null>(
    () => {
      const end = dayjs();
      const start = end.subtract(unsubscribeDays, "day");
      return [start, end];
    }
  );
  const [debounceTimer, setDebounceTimer] = React.useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const isUserChangeRef = React.useRef(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleViewBranch = (branchId: number) => {
    navigate(`/reportes/documentos/${branchId}`);
  };

  const calculateDays = (start: Dayjs, end: Dayjs): number => {
    return end.diff(start, "day");
  };

  const handleDateChange = (dates: (Dayjs | null)[] | null) => {
    if (dates && dates[0] && dates[1]) {
      isUserChangeRef.current = true;
      setDateRange(dates);
      const days = calculateDays(dates[0], dates[1]);

      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(() => {
        setUnsubscribeDays(days);
        getUnsubscribeStats(undefined, startDate, endDate).then(
          setUnsubscribeStats
        );
        if (onDateRangeChange) {
          onDateRangeChange(startDate, endDate);
        }
        isUserChangeRef.current = false;
      }, 500);

      setDebounceTimer(timer);
    }
  };

  React.useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  if (!unsubscribeStats) {
    return null;
  }

  return (
    <div
      style={{
        ...styles.chartCard,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={styles.chartHeader}>
        <div style={styles.chartHeaderContent}>
          <h3 style={styles.chartTitle}>
            <i
              className="fa-solid fa-user-minus"
              style={{ ...styles.chartTitleIcon, color: "#ef4444" }}
            ></i>
            Información de colaboradores
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setPickerOpen(!pickerOpen)}
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6b7280",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                }}
                title="Seleccionar rango de fechas"
              >
                <i
                  className="fa-regular fa-calendar"
                  style={{ fontSize: "14px" }}
                ></i>
              </button>
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  zIndex: 1000,
                  visibility: pickerOpen ? "visible" : "hidden",
                  opacity: pickerOpen ? 1 : 0,
                }}
              >
                <RangePicker
                  open={pickerOpen}
                  onOpenChange={(open) => setPickerOpen(open)}
                  format="DD [de] MMMM [de] YYYY"
                  placeholder={["Fecha de inicio", "Fecha de fin"]}
                  size="small"
                  allowClear={false}
                  value={dateRange}
                  onChange={handleDateChange}
                  locale={locale}
                  style={{
                    width: "0px",
                    height: "0px",
                    padding: 0,
                    border: "none",
                    overflow: "hidden",
                    position: "absolute",
                  }}
                  renderExtraFooter={() => <div style={{ padding: 8 }} />}
                />
              </div>
            </div>
            <button
              onClick={() => {
                const ws = utils.aoa_to_sheet([]);
                const headers = [
                  "Sucursal",
                  "Bajas Recientes",
                  "Bajas Totales",
                  "Activos",
                  "Total",
                ];
                utils.sheet_add_aoa(ws, [headers], { origin: "A1" });
                let rowIndex = 2;
                unsubscribeStats.byBranch.forEach((item: any) => {
                  const row = [
                    item.branch.name,
                    item.recentUnsubscribes,
                    item.totalUnsubscribes,
                    item.activePartners,
                    item.totalPartners,
                  ];
                  utils.sheet_add_aoa(ws, [row], {
                    origin: `A${rowIndex}`,
                  });
                  rowIndex++;
                });
                ws["!cols"] = [
                  { wch: 35 },
                  { wch: 18 },
                  { wch: 15 },
                  { wch: 12 },
                  { wch: 12 },
                ];
                const wb = utils.book_new();
                utils.book_append_sheet(wb, ws, "Bajas por Sucursal");
                writeFile(wb, `Bajas_Ultimos_${unsubscribeDays}_dias.xlsx`);
              }}
              style={{
                ...styles.exportChartButton,
                padding: "6px 10px",
              }}
              title="Descargar Excel"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#16a34a";
                e.currentTarget.style.borderColor = "#16a34a";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#22c55e";
                e.currentTarget.style.borderColor = "#22c55e";
              }}
            >
              <i
                className="fa-solid fa-file-arrow-down"
                style={{ fontSize: "12px", color: "#fff" }}
              ></i>
            </button>
          </div>
        </div>
      </div>
      <div
        style={{
          ...styles.chartBody,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
          <div
            style={{
              ...styles.unsubscribeStatCardSmall,
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            }}
          >
            <div style={styles.unsubscribeStatIconSmall}>
              <i className="fa-solid fa-arrow-trend-down"></i>
            </div>
            <div>
              <div style={styles.unsubscribeStatValueSmall}>
                {unsubscribeStats.totals.recentUnsubscribes}
              </div>
              <div style={styles.unsubscribeStatLabelSmall}>
                {dateRange
                  ? `${dateRange[0].format("DD/MM")} - ${dateRange[1].format(
                      "DD/MM"
                    )}`
                  : `Últimos ${unsubscribeDays}d`}
              </div>
            </div>
          </div>
          <div
            style={{
              ...styles.unsubscribeStatCardSmall,
              background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
            }}
          >
            <div style={styles.unsubscribeStatIconSmall}>
              <i className="fa-solid fa-users"></i>
            </div>
            <div>
              <div style={styles.unsubscribeStatValueSmall}>
                {unsubscribeStats.totals.activePartners}
              </div>
              <div style={styles.unsubscribeStatLabelSmall}>Activos</div>
            </div>
          </div>
        </div>

        {unsubscribeStats.byBranch.filter((b: any) => b.recentUnsubscribes > 0)
          .length > 0 ? (
          <div
            style={{
              ...styles.branchTableContainer,
              maxHeight: "280px",
              overflowY: "auto",
              flex: 1,
            }}
          >
            <table style={styles.branchTable}>
              <thead>
                <tr style={styles.branchTableHeaderRow}>
                  <th
                    style={{
                      ...styles.branchTableHeader,
                      fontSize: "11px",
                      padding: "8px",
                    }}
                  >
                    Sucursal
                  </th>
                  <th
                    style={{
                      ...styles.branchTableHeader,
                      fontSize: "11px",
                      padding: "8px",
                    }}
                  >
                    Bajas
                  </th>
                  <th
                    style={{
                      ...styles.branchTableHeader,
                      fontSize: "11px",
                      padding: "8px",
                    }}
                  >
                    Activos
                  </th>
                  <th
                    style={{
                      ...styles.branchTableHeader,
                      fontSize: "11px",
                      padding: "8px",
                    }}
                  >
                    %
                  </th>
                  <th
                    style={{
                      ...styles.branchTableHeader,
                      fontSize: "11px",
                      padding: "8px",
                    }}
                  >
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {unsubscribeStats.byBranch
                  .filter((b: any) => b.recentUnsubscribes > 0)
                  .map((item: any, index: number) => (
                    <tr key={index} style={styles.branchTableRow}>
                      <td
                        style={{
                          ...styles.branchTableCell,
                          fontSize: "11px",
                          padding: "6px 8px",
                        }}
                      >
                        <span title={item.branch.name}>
                          {item.branch.name.length > 18
                            ? item.branch.name.substring(0, 18) + "..."
                            : item.branch.name}
                        </span>
                      </td>
                      <td
                        style={{
                          ...styles.branchTableCell,
                          color: "#ef4444",
                          fontWeight: 600,
                          fontSize: "12px",
                          padding: "6px 8px",
                        }}
                      >
                        {item.recentUnsubscribes}
                      </td>
                      <td
                        style={{
                          ...styles.branchTableCell,
                          color: "#22c55e",
                          fontSize: "12px",
                          padding: "6px 8px",
                        }}
                      >
                        {item.activePartners}
                      </td>
                      <td
                        style={{
                          ...styles.branchTableCell,
                          fontSize: "11px",
                          padding: "6px 8px",
                        }}
                      >
                        <span
                          style={{
                            background:
                              item.totalPartners > 0 &&
                              (item.recentUnsubscribes / item.totalPartners) *
                                100 >
                                10
                                ? "#fef2f2"
                                : "#f0fdf4",
                            color:
                              item.totalPartners > 0 &&
                              (item.recentUnsubscribes / item.totalPartners) *
                                100 >
                                10
                                ? "#ef4444"
                                : "#22c55e",
                            padding: "2px 5px",
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: 600,
                          }}
                        >
                          {item.totalPartners > 0
                            ? (
                                (item.recentUnsubscribes / item.totalPartners) *
                                100
                              ).toFixed(1)
                            : 0}
                          %
                        </span>
                      </td>
                      <td
                        style={{
                          ...styles.branchTableCell,
                          fontSize: "11px",
                          padding: "6px 8px",
                        }}
                      >
                        <button
                          onClick={() => handleViewBranch(item.branch.id)}
                          style={styles.branchTableEyeButton}
                          title="Ver detalles de la sucursal"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#2563eb";
                            const icon = e.currentTarget.querySelector("i");
                            if (icon) icon.style.color = "#fff";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            const icon = e.currentTarget.querySelector("i");
                            if (icon) icon.style.color = "#2563eb";
                          }}
                        >
                          <i
                            className="fa-solid fa-eye"
                            style={styles.branchTableEyeIcon}
                          ></i>
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "30px",
              color: "#6b7280",
            }}
          >
            <i
              className="fa-solid fa-check-circle"
              style={{
                fontSize: "32px",
                color: "#22c55e",
                marginBottom: "10px",
              }}
            ></i>
            <p style={{ fontSize: "13px", fontWeight: 500, margin: 0 }}>
              {dateRange
                ? `¡Sin bajas del ${dateRange[0].format(
                    "DD/MM/YYYY"
                  )} al ${dateRange[1].format("DD/MM/YYYY")}!`
                : `¡Sin bajas en ${unsubscribeDays} días!`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chartCard: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    minWidth: 0,
    maxWidth: "100%",
    width: "100%",
  },
  chartHeader: {
    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
    padding: "16px 20px",
    borderBottom: "1px solid #e5e7eb",
  },
  chartHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",
  },
  chartTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    minWidth: 0,
  },
  chartTitleIcon: {
    fontSize: "20px",
    color: "#667eea",
  },
  exportChartButton: {
    background: "#22c55e",
    border: "1px solid #22c55e",
    borderRadius: "8px",
    padding: "8px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    minWidth: "auto",
  },
  chartBody: {
    padding: "16px",
  },
  unsubscribeStatCardSmall: {
    borderRadius: "10px",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  unsubscribeStatIconSmall: {
    width: "36px",
    height: "36px",
    background: "rgba(255, 255, 255, 0.25)",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    color: "#fff",
  },
  unsubscribeStatValueSmall: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#fff",
    lineHeight: 1,
  },
  unsubscribeStatLabelSmall: {
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.9)",
    marginTop: "2px",
  },
  branchTableContainer: {
    maxHeight: "280px",
    overflowY: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#fff",
  },
  branchTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  branchTableHeaderRow: {
    backgroundColor: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  branchTableHeader: {
    padding: "12px 16px",
    textAlign: "left",
    fontWeight: "600",
    color: "#374151",
    fontSize: "13px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  branchTableRow: {
    borderBottom: "1px solid #e5e7eb",
    transition: "background-color 0.2s ease",
    cursor: "default",
  },
  branchTableCell: {
    padding: "14px 16px",
    color: "#374151",
  },
  branchTableEyeButton: {
    background: "transparent",
    border: "1px solid #2563eb",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  },
  branchTableEyeIcon: {
    color: "#2563eb",
    fontSize: "14px",
    transition: "color 0.2s ease",
  },
};

export default InformacionColaboradores;
